import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import store, { RootState } from '../store';
import {
  fetchSettings,
  updateSettings,
} from '../features/settings/settingsSlice';
import logger from '../lib/logger';

// Output languages - matches transcription languages (excluding Auto)
export const OUTPUT_LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'ja', name: 'Japanese' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'it', name: 'Italian' },
  { code: 'nl', name: 'Dutch' },
  { code: 'af', name: 'Afrikaans' },
  { code: 'ar', name: 'Arabic' },
  { code: 'hy', name: 'Armenian' },
  { code: 'az', name: 'Azerbaijani' },
  { code: 'be', name: 'Belarusian' },
  { code: 'bs', name: 'Bosnian' },
  { code: 'bg', name: 'Bulgarian' },
  { code: 'ca', name: 'Catalan' },
  { code: 'zh', name: 'Chinese' },
  { code: 'hr', name: 'Croatian' },
  { code: 'cs', name: 'Czech' },
  { code: 'da', name: 'Danish' },
  { code: 'et', name: 'Estonian' },
  { code: 'fi', name: 'Finnish' },
  { code: 'gl', name: 'Galician' },
  { code: 'el', name: 'Greek' },
  { code: 'he', name: 'Hebrew' },
  { code: 'hi', name: 'Hindi' },
  { code: 'hu', name: 'Hungarian' },
  { code: 'is', name: 'Icelandic' },
  { code: 'id', name: 'Indonesian' },
  { code: 'kn', name: 'Kannada' },
  { code: 'kk', name: 'Kazakh' },
  { code: 'ko', name: 'Korean' },
  { code: 'lv', name: 'Latvian' },
  { code: 'lt', name: 'Lithuanian' },
  { code: 'mk', name: 'Macedonian' },
  { code: 'ms', name: 'Malay' },
  { code: 'mr', name: 'Marathi' },
  { code: 'mi', name: 'Maori' },
  { code: 'ne', name: 'Nepali' },
  { code: 'no', name: 'Norwegian' },
  { code: 'fa', name: 'Persian' },
  { code: 'pl', name: 'Polish' },
  { code: 'ro', name: 'Romanian' },
  { code: 'ru', name: 'Russian' },
  { code: 'sr', name: 'Serbian' },
  { code: 'sk', name: 'Slovak' },
  { code: 'sl', name: 'Slovenian' },
  { code: 'sw', name: 'Swahili' },
  { code: 'sv', name: 'Swedish' },
  { code: 'tl', name: 'Tagalog' },
  { code: 'ta', name: 'Tamil' },
  { code: 'th', name: 'Thai' },
  { code: 'tr', name: 'Turkish' },
  { code: 'uk', name: 'Ukrainian' },
  { code: 'ur', name: 'Urdu' },
  { code: 'vi', name: 'Vietnamese' },
  { code: 'cy', name: 'Welsh' },
];

// Get browser language, fallback to 'en' if not supported
const getBrowserLanguage = (): string => {
  const browserLang = navigator.language.split('-')[0];
  return (
    OUTPUT_LANGUAGES.find((lang) => lang.code === browserLang)?.code || 'en'
  );
};

// Convert language code to full language name
const getLanguageName = (languageCode: string): string => {
  const language = OUTPUT_LANGUAGES.find(
    (lang) => lang.code === languageCode.toLowerCase(),
  );
  return language?.name || 'English';
};

/**
 * Get current output language from Redux store.
 * Falls back to browser language if not set.
 */
export const getCurrentOutputLanguage = async (): Promise<string> => {
  const state = store.getState() as RootState;
  const outputLanguage =
    state.settings?.recorder?.otherSettings?.outputLanguage;
  return outputLanguage || getBrowserLanguage();
};

/**
 * Get current output language name (full name, not code).
 */
export const getCurrentOutputLanguageName = async (): Promise<string> => {
  const languageCode = await getCurrentOutputLanguage();
  return getLanguageName(languageCode);
};

// Hook for managing output language state
export const useOutputLanguage = () => {
  const outputLanguage = useSelector(
    (state: RootState) =>
      state.settings?.recorder?.otherSettings?.outputLanguage,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const effectiveLanguage = outputLanguage || getBrowserLanguage();

  const recorder = useSelector((state: RootState) => state.settings?.recorder);

  const updateOutputLanguage = useCallback(
    async (language: string): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const merged = {
          ...recorder,
          otherSettings: {
            ...recorder?.otherSettings,
            outputLanguage: language,
          },
        };
        await store
          .dispatch(updateSettings({ key: 'recorder', newSettings: merged }))
          .unwrap();
        return true;
      } catch (err) {
        setError('Failed to update output language');
        logger.error('Error updating output language:', err);

        // updateSettings is optimistic in pending state; refetch to restore
        // server truth when the API call fails.
        try {
          await store.dispatch(fetchSettings('recorder')).unwrap();
        } catch (refreshErr) {
          logger.error(
            'Failed to refresh recorder settings after output language update failure:',
            refreshErr,
          );
        }

        return false;
      } finally {
        setLoading(false);
      }
    },
    [recorder],
  );

  const currentLanguageName =
    OUTPUT_LANGUAGES.find((lang) => lang.code === effectiveLanguage)?.name ||
    'English';

  return {
    outputLanguage: effectiveLanguage,
    currentLanguageName,
    loading,
    error,
    updateOutputLanguage,
    clearError: () => setError(null),
  };
};
